import { FormControl, FormControlLabel, GridSize, Radio, RadioGroup, RadioGroupProps, Typography } from '@mui/material';
import styled, { css } from 'styled-components';

import { AppFormLabel } from 'components/core/AppFormLabel/AppFormLabel';
import React from 'react';
import { AppFormDescription } from 'components/core/AppFormDescription/AppFormDescription';
import Grid from 'components/core/Grid';

export type RadioGroupValue = string | number | boolean;

export interface AppRadioGroupProps {
  name?: string;
  title?: string;
  description?: string;
  subDescription?: string;
  hasCheckbox?: boolean;
  isSingleBar?: boolean;
  isSingleColumn?: boolean;
  isRow?: boolean;
  isGrey?: boolean;
  isNarrow?: boolean;
  titleStyle?: any;
  subtitleStyle?: any;
  subtitle2Style?: any;
  isPaddingNarrow?: boolean;
  buttons: {
    title: string;
    subtitle?: string | React.ReactNode;
    subtitle2?: string | React.ReactNode;
    value: RadioGroupValue;
    onClick?: () => void;
    recommendedText?: string;
    RightComponent?: JSX.Element;
    MainComponent?: JSX.Element;
    isDisabled?: boolean;
  }[];
  value: RadioGroupValue;
  variant?: 'basic' | 'cardSelect';

  setValue: (val: RadioGroupValue) => void;
  disabled?: boolean;
  sm?: GridSize;
  md?: GridSize;
  lg?: GridSize;
}

const StyledFormControl = styled(FormControl)`
  width: 100%;
`;

const StyledRadioGroup = styled(RadioGroup) <{
  $hasCheckbox: boolean;
  $isSingleBar?: boolean;
  $isRow?: boolean;
  $isSingleColumn?: boolean;
}>`
  ${props =>
    props.$isSingleBar &&
    css`
      flex-direction: row;
    `}
  ${props =>
    props.$isRow &&
    css`
      flex-direction: row;
      gap: ${props => props.theme.spacing(2)};
    `}
  ${props =>
    !props.$hasCheckbox &&
    css`
      & .MuiSvgIcon-root {
        display: none;
      }
    `}
`;

const StyledFormControlLabel = styled(FormControlLabel) <{
  $isSelected: boolean;
  $isPaddingNarrow: boolean;
  $isSingleBar: boolean;
  $isRow: boolean;
  $isSingleColumn: boolean;
  $isGrey: boolean;
  $isNarrow: boolean;
}>`
  background-color: #fff;
  width: 100%;
  margin: 2px;
  border: 1px solid ${props => props.theme.palette.grey[400]};
  border-radius: 5px;
  padding: ${props => props.theme.spacing(2)} ${props => props.theme.spacing(0.5)};
  color: ${props => props.theme.palette.grey[700]};

  & .MuiSvgIcon-root {
    color: ${props => props.theme.palette.grey[400]};
  }

  ${props =>
    props.$isPaddingNarrow &&
    css`
    padding-top: ${props => props.theme.spacing(1)} !important;         
    padding-bottom: ${props => props.theme.spacing(1)} !important;         
  `}
  ${props =>
    props.$isSelected &&
    css`
      background-color: #f6fcff;
      border: 1px solid ${props => props.theme.palette.secondary.main} !important;
      & .MuiSvgIcon-root {
        color: ${props => props.theme.palette.secondary.main};
      }
    `}
  ${props =>
    props.$isSingleBar &&
    css`
      flex: 1;
      margin: 0 !important;
      border-radius: 0px;
      flex-direction: column;
      text-align: center;
      justify-content: center;
      padding: 0;
      padding-bottom: ${props => props.theme.spacing(1)};
      &:first-child {
        border-top-left-radius: 5px;
        border-bottom-left-radius: 5px;
      }
      &:last-child {
        border-top-right-radius: 5px;
        border-bottom-right-radius: 5px;
      }
    `}
    ${props =>
    props.$isSingleColumn &&
    css`
      flex: 1;
      margin: 0 !important;
      border-radius: 0px;
      

      &:first-child {
        border-top-left-radius: 5px;
        border-top-right-radius: 5px;
      }
      &:last-child {
        border-bottom-left-radius: 5px;
        border-bottom-right-radius: 5px;
      }
      &:not(:first-child) {
        border-top-color: transparent;
      }
    `}
    ${props => props.$isSingleColumn && props.$isSelected &&
    css`
        border-color: ${props => props.theme.palette.secondary.main};
        & + label {
          border-top-color: transparent !important;
        }
      `}
    ${props =>
    props.$isRow &&
    css`
      margin: 0 !important;
      padding-right: ${props => props.theme.spacing(3)};
      flex: 1;
    `}
    ${props =>
    props.$isNarrow &&
    css`
      padding: ${props => props.theme.spacing(0.5)};
    `}

  &.MuiFormControlLabel-root {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  &:hover {
    background-color: #f6fcff;
    border: 1px solid ${props => props.theme.palette.secondary.main};
  }
  &:not(:first-child) {
    margin-top: ${props => props.theme.spacing(1)};
  }
`;

const CardSelectFormControlLabel = styled(FormControlLabel) <{
  $isSelected: boolean;
  $isPaddingNarrow: boolean;
}>`
  background-color: #fff;
  border: 1px solid ${props => props.theme.palette.grey[400]};
  border-radius: 5px;
  padding: ${props => props.theme.spacing(2)};
  padding-top: ${props => props.theme.spacing(1)};
  color: ${props => props.theme.palette.grey[700]};
  flex-direction: column;
  flex: 1;
  width: 100%;
  height: 100%;
  margin-right: 0;
  margin-left: 0;
  position: relative;
  overflow: hidden;
  align-items: stretch;

  & .MuiSvgIcon-root {
    color: ${props => props.theme.palette.grey[400]};
    ${props => props.theme.breakpoints.down('sm')} {
      display: none;
    }
  }
  &:hover {
    background-color: ${props => props.theme.palette.grey[50]};
    border: 1px solid ${props => props.theme.palette.secondary.main};
  }
  ${props =>
    props.$isSelected &&
    css`
      background-color: #f6fcff;
      border: 1px solid ${props => props.theme.palette.secondary.main};
      color: ${props => props.theme.palette.grey[900]};
      & .MuiSvgIcon-root {
        color: ${props => props.theme.palette.secondary.main};
      }
    `}
  ${props =>
    props.$isPaddingNarrow &&
    css`
    padding-left: ${props => props.theme.spacing(0.5)};     
    padding-right: ${props => props.theme.spacing(0.5)};     
    padding-bottom: ${props => props.theme.spacing(1)};     
  `}
    
  &:after {
    content: 'Choose';
    width: 100%;
    border: 1px solid ${props => props.theme.palette.grey[300]};
    border-radius: 5px;
    text-align: center;
    padding: ${props => props.theme.spacing(1)};
    margin-top: auto;
    background-color: #fff;
    transition: background-color 0.2s;
    ${props =>
    props.$isSelected &&
    css`
        content: 'Selected';
        background-color: ${props => props.theme.palette.secondary.main};
        color: #fff;
        font-weight: 500;
        border-color: transparent;
      `}
  }
`;

const RecommendedTag = styled.div`
  position: absolute;
  top: 0px;
  right: 0px;
  background-color: ${props => props.theme.palette.secondary.main};
  color: #fff;
  padding: 2px 8px;
  border-bottom-left-radius: 5px;
  font-size: 10px;
  font-weight: 500;
`;

const SubTitleTypography = styled(Typography) <{ $isSingleBar: boolean }>`
  ${props =>
    props.$isSingleBar &&
    css`
      width: 80%;
      margin: auto;
    `}
`;

const defaultSMProp = 6;
const defaultMDProp = 4;
const defaultLGProp = 3;

export function AppRadioGroup({
  name,
  title,
  description,
  subDescription,
  buttons,
  value,
  isSingleBar = false,
  hasCheckbox = true,
  isSingleColumn = false,
  isRow = false,
  isGrey = false,
  isNarrow = false,
  setValue,
  titleStyle,
  variant = 'basic',
  isPaddingNarrow = false,
  subtitleStyle,
  subtitle2Style,
  disabled = false,
  sm = defaultSMProp,
  md = defaultMDProp,
  lg = defaultLGProp,
  ...rest
}: AppRadioGroupProps & RadioGroupProps) {
  const isBasic = variant === 'basic';
  const isCardSelect = variant === 'cardSelect';
  const onChange = (_e: React.ChangeEvent<HTMLInputElement>, value: string) => {
    if (buttons.length == 0) return;
    // Radio groups convert every value into a string, parse the wanted type here and use that to get the value back
    const enteredType = typeof buttons[0].value;

    if (enteredType === 'string') {
      setValue(value);
    } else if (enteredType === 'boolean') {
      if (value === 'true') setValue(true);
      if (value === 'false') setValue(false);
    } else if (enteredType === 'number') {
      setValue(parseInt(value));
    }
  };

  return (
    <>
      {isBasic && (
        <StyledFormControl>
          {title ? <AppFormLabel title={title} /> : null}
          {description ? <AppFormDescription>{description}</AppFormDescription> : null}
          {subDescription ? <AppFormDescription>{subDescription}</AppFormDescription> : null}

          <StyledRadioGroup
            name={name}
            value={value}
            onChange={onChange}
            {...rest}
            $hasCheckbox={hasCheckbox}
            $isSingleBar={isSingleBar}
            $isSingleColumn={isSingleColumn}
            $isRow={isRow}
          >
            <RadioButtons
              buttons={buttons}
              value={value}
              disabled={disabled}
              isSingleBar={isSingleBar}
              isSingleColumn={isSingleColumn}
              isPaddingNarrow={isPaddingNarrow}
              isRow={isRow}
              titleStyle={titleStyle}
              subtitleStyle={subtitleStyle}
              subtitle2Style={subtitle2Style}
              isGrey={isGrey}
              isNarrow={isNarrow}
              sm={sm}
              md={md}
              lg={lg}
            />
          </StyledRadioGroup>
        </StyledFormControl>
      )}
      {/* TODO - This whole component needs to be rebuilt. This is supposed to be a universal, app wide radio group. However, we now have a "CardSelect" option that is only 
      used in one component. The Card Select Component should be built from the AppRadioGroup, not inside of it. Due to a tight deadline, we will incur this debt. 2022-08-17 */}
      {isCardSelect && (
        <StyledFormControl>
          {title ? <AppFormLabel title={title} /> : null}
          {description ? <AppFormDescription>{description}</AppFormDescription> : null}
          {subDescription ? <AppFormDescription>{subDescription}</AppFormDescription> : null}
          <StyledRadioGroup name={name} value={value} onChange={onChange} {...rest} row $hasCheckbox={hasCheckbox}>
            <RadioButtons
              buttons={buttons}
              value={value}
              disabled={disabled}
              isSingleBar={isSingleBar}
              isRow={isRow}
              titleStyle={titleStyle}
              subtitleStyle={subtitleStyle}
              variant={variant}
              isPaddingNarrow={isPaddingNarrow}
              sm={sm}
              md={md}
              lg={lg}
            />
          </StyledRadioGroup>
        </StyledFormControl>
      )}
    </>
  );
}

interface RadioButtonsProps
  extends Pick<
    AppRadioGroupProps,
    | 'buttons'
    | 'value'
    | 'disabled'
    | 'isSingleBar'
    | 'isSingleColumn'
    | 'isRow'
    | 'titleStyle'
    | 'subtitleStyle'
    | 'subtitle2Style'
    | 'variant'
    | 'isPaddingNarrow'
    | 'isGrey'
    | 'isNarrow'
  > {
  sm?: GridSize;
  md?: GridSize;
  lg?: GridSize;
}

const RadioButtons = ({
  buttons,
  value,
  disabled,
  titleStyle,
  subtitleStyle,
  subtitle2Style,
  sm = defaultSMProp,
  md = defaultMDProp,
  lg = defaultLGProp,
  isSingleBar = false,
  isSingleColumn = false,
  isRow = false,
  isGrey = false,
  isNarrow = false,
  isPaddingNarrow = false,
  variant = 'basic',
}: RadioButtonsProps) => {
  const isBasic = variant === 'basic';
  const isCardSelect = variant === 'cardSelect';
  return (
    <>
      {isBasic && (
        <>
          {buttons.map(button => (
            <StyledFormControlLabel
              disabled={disabled || button.isDisabled}
              key={`${button.value}`}
              value={button.value}
              $isSingleBar={isSingleBar}
              $isSingleColumn={isSingleColumn}
              $isRow={isRow}
              $isPaddingNarrow={isPaddingNarrow}
              $isGrey={isGrey}
              $isNarrow={isNarrow}
              control={<Radio onClick={button.onClick} color="secondary" />}
              label={
                <>
                  <Typography variant="body2" style={titleStyle}>
                    <strong>{button.title}</strong>
                  </Typography>
                  <SubTitleTypography variant="body2" $isSingleBar={isSingleBar} style={subtitleStyle}>
                    {button?.subtitle}
                  </SubTitleTypography>
                  <SubTitleTypography variant="body2" $isSingleBar={isSingleBar} style={subtitle2Style}>
                    {button?.subtitle2}
                  </SubTitleTypography>
                </>
              }
              checked={value === button.value}
              $isSelected={button.value === value}
            />
          ))}
        </>
      )}
      {isCardSelect && (
        <Grid container spacing={2} alignItems="stretch">
          {buttons.map(button => (
            <Grid key={button.value} item xs={12} sm={sm} md={md} lg={lg} style={{ display: 'flex' }}>
              <CardSelectFormControlLabel
                disabled={disabled || button.isDisabled}
                key={`${button.value}`}
                value={button.value}
                control={<Radio onClick={button.onClick} color="secondary" />}
                $isPaddingNarrow={isPaddingNarrow}
                label={
                  <>
                    {button.recommendedText && (
                      <RecommendedTag>{button.recommendedText ? button.recommendedText : 'Recommended'}</RecommendedTag>
                    )}
                    <Typography variant="body1" style={titleStyle} align="center">
                      <strong>{button.title}</strong>
                    </Typography>
                    {button.RightComponent}
                    <SubTitleTypography variant="body2" style={subtitleStyle} $isSingleBar={false}>
                      {button?.subtitle}
                    </SubTitleTypography>
                    {button.MainComponent}
                  </>
                }
                checked={value === button.value}
                $isSelected={button.value === value}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );
};
